/**
 * Groups management functions
 * Migrated from js.php
 */

import { toastSuccess, toastError } from "../ui/messages";
import { refreshModal, closeModalLast } from "../ui/modal";
import { api } from "../api";
import { onlygroup, onlygroupartist } from "./resellerDashboard";
import { getDomElement, queryAll, delegate } from "../utils/dom";
import { renderGroupSelect } from "./components/GroupSelect";

// Delegation: bind onchange for dynamically inserted #groupselect
delegate("change", "#groupselect", (_e, el) => {
  onlygroup((el as HTMLSelectElement).value);
});

// Collect the ids of all checked `.lightbox-toggles` (state === "1") as strings.
function selectedToggleIds(): string[] {
  const ids: string[] = [];
  queryAll(".lightbox-toggles").forEach((el) => {
    const theId = el.id;
    if (!theId) return;
    const lightboxId = theId.replace("lb-state-", "");
    const stateEl = getDomElement<HTMLInputElement>("lb-state-" + lightboxId);
    if (stateEl?.value === "1") ids.push(lightboxId);
  });
  return ids;
}

export async function removegroup(group_id: string): Promise<void> {
  if (!confirm("Are you sure you want to remove this group?")) return;

  try {
    const response = await api.groups.removeResellerGroup({ id: group_id });
    toastSuccess("Group removed");
    setTimeout(function () {
      refreshModal();
    }, 100);
    renderGroupSelect(response.groups, "-- Partner groups --");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function removegroupartist(group_id: string): Promise<void> {
  if (!confirm("Are you sure you want to remove this group?")) return;

  try {
    const response = await api.groups.removeArtistGroup({ id: group_id });
    toastSuccess("Group removed");
    setTimeout(function () {
      refreshModal();
    }, 100);
    renderGroupSelect(response.groups, "-- Artist groups --");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

/**
 * Add partners to groups
 */
export async function addpartnerstogroups(): Promise<void> {
  const groups = selectedToggleIds();

  if (groups.length === 0) {
    toastError("Error - no groups selected");
    return;
  }

  const selectedPartnersEl = getDomElement<HTMLInputElement>("selectedpartners");
  if (!selectedPartnersEl?.value) {
    toastError("Error - no partners selected");
    return;
  }

  try {
    const response = await api.groups.addResellersToGroups({
      partners: selectedPartnersEl.value
        .split(",")
        .map(Number)
        .filter((n) => n > 0),
      groups,
    });
    closeModalLast();
    toastSuccess("Partners added to groups");
    renderGroupSelect(response.groups, "-- Partner groups --");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

/**
 * Add artists to groups
 */
export async function addartiststogroups(): Promise<void> {
  const groups = selectedToggleIds();

  if (groups.length === 0) {
    toastError("Error - no groups selected");
    return;
  }

  const selectedPartnersEl = getDomElement<HTMLInputElement>("selectedpartners");
  if (!selectedPartnersEl?.value) {
    toastError("Error - no artists selected");
    return;
  }

  try {
    const response = await api.groups.addArtistsToGroups({
      partners: selectedPartnersEl.value
        .split(",")
        .map(Number)
        .filter((n) => n > 0),
      groups,
    });
    closeModalLast();
    toastSuccess("Artists added to groups");
    renderGroupSelect(response.groups, "-- Artist groups --");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function blockuserfromresellers(user_id: string): Promise<void> {
  const block = selectedToggleIds()
    .map(Number)
    .filter((n) => n > 0);

  try {
    await api.users.blockFromResellers({ id: Number(user_id), block });
    closeModalLast();
    toastSuccess("Blocks saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function blockuserfromresellers2(user_id: string): Promise<void> {
  const block = selectedToggleIds()
    .map(Number)
    .filter((n) => n > 0);

  try {
    await api.users.reverseBlock({ id: Number(user_id), block });
    closeModalLast();
    toastSuccess("Allowed resellers saved");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savegroupmembers(group_id: string): Promise<void> {
  const save = selectedToggleIds()
    .map(Number)
    .filter((n) => n > 0);

  if (save.length) {
    const groupselectEl = getDomElement<HTMLInputElement>("groupselect");
    const groupselect = groupselectEl?.value ?? "";

    try {
      const response = await api.groups.saveResellerGroupMembers({
        id: group_id,
        save,
      });
      closeModalLast();
      toastSuccess("Group members updated");
      setTimeout(function () {
        refreshModal();
      }, 500);
      // Guarded as the original was: the select is only on the resellers
      // dashboard, and the filter should only be re-applied when it is.
      if (getDomElement("partnergroup-select")) {
        renderGroupSelect(response.groups, "-- Partner groups --");
        if (groupselect === group_id) {
          onlygroup(group_id);
        }
      }
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function savegroupmembersartist(group_id: string): Promise<void> {
  const save = selectedToggleIds()
    .map(Number)
    .filter((n) => n > 0);

  if (save.length) {
    const groupselectEl = getDomElement<HTMLInputElement>("groupselect");
    const groupselect = groupselectEl?.value ?? "";

    try {
      const response = await api.groups.saveArtistGroupMembers({
        id: group_id,
        save,
      });
      closeModalLast();
      toastSuccess("Group members updated");
      setTimeout(function () {
        refreshModal();
      }, 500);
      // Guarded as the original was: the select is only on the resellers
      // dashboard, and the filter should only be re-applied when it is.
      if (getDomElement("partnergroup-select")) {
        renderGroupSelect(response.groups, "-- Artist groups --");
        if (groupselect === group_id) {
          onlygroupartist(group_id);
        }
      }
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}
